import type { Issue } from '../types/issue';
import type { DataModel } from './model';
import { capTotalForRelease, type CapMap } from './capacity';
import { detectRelease, releasesOfIssue, releasesOfIssueStrict } from './release-detect';
import { isDone, REFINED_PLUS, DONE_STATUSES } from './status';
import { num, secondsToDays, fmtInt } from './format';
import { statusKey } from './status';
import { resolvedOf, createdOf } from './jira-dates';
import type { ReleaseCal } from './releases';

// ─── Types ──────────────────────────────────────────────────────────────

export type ColumnKey = 'kapaPlan' | 'kapa' | 'initial' | 'epicGrob' | 'refine' | 'ist';

export type Column = {
  key: ColumnKey;
  label: string;
  subLabel?: string;
  // Permanent sichtbare Mehrzeilen-Erläuterung unter der Säule (Quelle,
  // einbezogene Status, Hinweise) — eine Zeile pro Eintrag.
  detailsLines?: string[];
  // Daten-Qualitäts-Warnung (z.B. wenn nur 10 von 96 Items ein
  // Original Estimate gepflegt haben → Säule zeigt nicht das ganze Bild)
  qualityWarning?: string;
  stories: number;
  bugs: number;
  editable?: boolean;
  isDefault?: boolean;   // bei kapaPlan: Default-Wert, sobald initialPlan gepflegt → wird zu kapa+initial
  initKey?: string;      // Key für state.initialPlan
  tooltip: string;
  // Optional: Aggregat-%-Verhältnisse (nur bei der Ist-Säule befüllt)
  vsKapaPct?:   number | null;  // Ist / Kapa-Total × 100
  vsRoughPct?:  number | null;  // Ist / Σ Rough × 100
  vsDetailPct?: number | null;  // Ist / Detail-Plan × 100
  // Quellen-Aufteilung (nur bei Ist)
  nFromTime?: number;           // Items, deren Ist aus Time Spent kam
  nFromPlan?: number;           // Items, deren Ist aus Refinement-Plan (Fallback) kam
  // Zwei alternative Ist-Sichten (nur Ist-Säule):
  //
  // Variante A — Time Spent mit Estimate-Fallback:
  //   Items mit Time Spent → TS-Wert; Items ohne TS → Original Estimate;
  //   Items ohne beides → fallen raus.
  //   Beste Annäherung an „echten Aufwand", wo es geht.
  tsOrEstStoriesPT?: number;
  tsOrEstBugsPT?:    number;
  nTsS?: number;             // Stories mit Time Spent (TS-Wert verwendet)
  nTsB?: number;             // Bugs mit Time Spent
  nEstFallbackS?: number;    // Stories ohne TS, Estimate als Ersatz
  nEstFallbackB?: number;    // Bugs ohne TS, Estimate als Ersatz
  nNeitherS?: number;        // Stories ohne beides (zählen nicht)
  nNeitherB?: number;        // Bugs ohne beides
  //
  // Variante B — nur Time Spent (Items ohne TS = 0):
  //   Reine „tatsächlich geloggte Zeit"-Sicht. Untergrenze.
  tsOnlyStoriesPT?: number;
  tsOnlyBugsPT?:    number;
  nNoTsS?: number;           // Stories ohne TS (beitragen 0)
  nNoTsB?: number;           // Bugs ohne TS

  nIstDoneTotal?: number;    // alle done-Items im Fenster (für Coverage)

  // Outlier-Cap-Reporting (Items, deren Wert auf 10 PT gekappt wurde)
  nRefCapped?:    number;    // Detail-Säule: Estimate >10 PT gekappt
  nIstEstCapped?: number;    // Ist-Säule:    Estimate >10 PT gekappt
  nIstTsCapped?:  number;    // Sub-Stages:   Time Spent >10 PT gekappt
  itemPtCap?:     number;    // verwendeter Cap-Wert (für Captions)
};

export type InitialPlanEntry = { stories: number; bugs: number };
export type InitialPlanMap   = Record<string, InitialPlanEntry>;

export type ComputeColumnsInput = {
  blockName: string;
  rows: Issue[];                    // alle Rows (für refine + ist; nicht gefiltert)
  model: DataModel;                 // für releaseOfIssue + Epic-Suche
  knownReleases: string[];          // für detectRelease/releaseOfIssue
  capacityByTeam: CapMap;
  initialPlan: InitialPlanMap;
  spToPtFactor: number;
  groupMode: 'release' | 'team' | 'assignee' | 'none';
  teamFilter: string;               // aus der Team-Bar oben ('' = alle)
  blockReleaseFilter: string;       // bei groupMode='team' das gewählte Release ('' = alle)
  releaseCal: Record<string, ReleaseCal>;  // für Resolved-Datum-Range pro Release
};

// ─── Hauptberechnung ────────────────────────────────────────────────────

export function computeColumns(opts: ComputeColumnsInput): Column[] {
  const {
    blockName, rows, model, knownReleases, capacityByTeam, initialPlan,
    spToPtFactor: sp2pt, groupMode, teamFilter, blockReleaseFilter, releaseCal,
  } = opts;

  // Release-Zeitfenster für die Ist-Säule. Bei groupMode='team' nutzen wir
  // den Block-Release-Filter (falls gesetzt), sonst blockName.
  const istReleaseName = (groupMode === 'team' && blockReleaseFilter) ? blockReleaseFilter : blockName;
  const istCal = releaseCal[istReleaseName];
  const istStart = istCal?.start ? new Date(istCal.start) : null;
  const istEnd   = istCal?.end   ? new Date(istCal.end)   : null;
  const hasTimeWindow = Boolean(istStart && istEnd);
  const resolvedInRange = (r: Issue): boolean => {
    if (!hasTimeWindow) return true; // ohne Datum: alle closed Items zulassen
    const d = resolvedOf(r);
    if (!d) return false;
    return d >= istStart! && d <= istEnd!;
  };
  // Für Detail: Item zählt nur, wenn es zur Release-Zeit überhaupt existiert
  // (Created ≤ Release-Ende). Karteileichen, die längst nach dem Release
  // verschoben wurden, fallen damit raus. Keine Untergrenze, weil ein
  // älteres Item mit Fix Version XX legitim noch dazugehören kann.
  const createdBeforeReleaseEnd = (r: Issue): boolean => {
    if (!hasTimeWindow) return true;
    const c = createdOf(r);
    if (!c) return true; // ohne Created-Datum: nicht ausschließen
    return c <= istEnd!;
  };
  let nIstSkippedOutOfRange = 0;
  let nRefSkippedOutOfRange = 0;

  // ── Outlier-Cap pro Item ────────────────────────────────────────────
  // Ein einzelnes Story/Bug-Item wird auf max. 10 PT gekappt. Anomalien
  // wie CAL-94568 (977 PT in Σ Time Spent durch Sub-Task-Aggregat oder
  // versehentlich falsch erfasste Estimate-Werte) verzerren die Säulen
  // sonst massiv. Cap greift auf Item-Ebene für Estimate UND Time Spent.
  // Epic-Werte (Rough Estimation, Σ Estimate auf Epic-Ebene) werden NICHT
  // gekappt — dort sind größere Werte legitim.
  const ITEM_PT_CAP = 10;
  const cap = (v: number) => v > ITEM_PT_CAP ? ITEM_PT_CAP : v;

  // ── Block-Zugehörigkeit ─────────────────────────────────────────────
  const epicInBlock = (e: Issue): boolean => {
    if ((e as Issue & { _isOrphanContainer?: boolean })._isOrphanContainer) return false;
    if (groupMode === 'team') {
      if (e['Team Name'] !== blockName) return false;
      return !blockReleaseFilter || detectRelease(e, knownReleases) === blockReleaseFilter;
    }
    if (groupMode === 'assignee') return e['Assignee'] === blockName;
    if (groupMode === 'none')     return true;
    return detectRelease(e, knownReleases) === blockName;
  };
  // issueInBlock: ein Item gehört zum Block, wenn EINE seiner Fix Versions
  // zum aktuellen Release matched (Multi-Release-Zuordnung). Items mit
  // ["2025 R3","2026 R1"] zählen also bei BEIDEN Releases.
  //
  // strict=false (Default): Items ohne eigene Fix Version dürfen das
  //                          Release ihres Parent-Epics erben.
  // strict=true            : nur Items mit eigener, zum Release passender
  //                          Fix Version. Wird in der Detail-Säule (und
  //                          Ist-Säule, die Detail-Teilmenge ist) genutzt,
  //                          damit dort wirklich nur Items reinfallen,
  //                          die das Release auf Item-Ebene zeigen.
  const issueInBlock = (r: Issue, strict = false): boolean => {
    const releases = strict
      ? releasesOfIssueStrict(r, knownReleases)
      : releasesOfIssue(r, model.byKey, knownReleases);
    if (groupMode === 'team') {
      if (r['Team Name'] !== blockName) return false;
      if (!blockReleaseFilter) return true;
      return releases.includes(blockReleaseFilter);
    }
    if (groupMode === 'assignee') return r['Assignee'] === blockName;
    if (groupMode === 'none')     return true;
    return releases.includes(blockName);
  };
  const matchTeam = (r: Issue): boolean => !teamFilter || r['Team Name'] === teamFilter;

  // ── 1) Kapazität (team-aware + ggf. Block-Release-Filter) ───────────
  const teamOpt = groupMode === 'team'
    ? { team: blockName, onlyRelease: blockReleaseFilter || undefined }
    : (teamFilter ? { team: teamFilter } : {});
  const capStories = capTotalForRelease(capacityByTeam, blockName, 's', teamOpt);
  const capBugs    = capTotalForRelease(capacityByTeam, blockName, 'b', teamOpt);

  // ── 2) Initial-Plan ─────────────────────────────────────────────────
  // Bei team-Block mit Release-Filter eigener Key 'Team::Release', sonst nur blockName.
  const initKey = (groupMode === 'team' && blockReleaseFilter)
    ? `${blockName}::${blockReleaseFilter}`
    : blockName;
  const ipRaw = initialPlan[initKey];
  const initStories = ipRaw ? (num(ipRaw.stories) ?? 0) : capStories;
  const initBugs    = ipRaw ? (num(ipRaw.bugs)    ?? 0) : capBugs;

  // ── 3) Grobplanung (Epic-Ebene) ─────────────────────────────────────
  // Pro Epic = Σ Rough Estimation + Σ Original Estimate (User-Wunsch).
  // Anteilig auf Stories/Bugs nach Σ Original Estimate der Kids verteilt.
  // SP werden ignoriert.
  let epicGrobS = 0, epicGrobB = 0, nEpicsRough = 0, nEpicsEst = 0, nEpicsBoth = 0;
  for (const e of model.epics) {
    if (!epicInBlock(e) || !matchTeam(e)) continue;
    const rough   = num(e['Custom field (Rough Estimation (d))']) ?? 0;
    const epicEst = secondsToDays(e['Σ Original Estimate']) ?? 0;
    const epicPlan = (rough > 0 ? rough : 0) + (epicEst > 0 ? epicEst : 0);
    if (epicPlan <= 0) continue;
    if (rough > 0)   nEpicsRough++;
    if (epicEst > 0) nEpicsEst++;
    if (rough > 0 && epicEst > 0) nEpicsBoth++;
    const kids = (e._kids ?? []).filter(matchTeam);
    let sLoad = 0, bLoad = 0;
    for (const k of kids) {
      const t = k['Issue Type'];
      if (t !== 'Story' && t !== 'Bug') continue;
      // Nur Σ Original Estimate; Story Points werden ignoriert.
      const est = secondsToDays(k['Σ Original Estimate']);
      const v = (est != null && est > 0) ? est : 0;
      if (t === 'Story') sLoad += v; else bLoad += v;
    }
    if (sLoad + bLoad > 0) {
      epicGrobS += epicPlan * (sLoad / (sLoad + bLoad));
      epicGrobB += epicPlan * (bLoad / (sLoad + bLoad));
    } else {
      // Fallback: Kid-Count, wenn nichts gepflegt
      const sCount = kids.filter(k => k['Issue Type'] === 'Story').length;
      const bCount = kids.filter(k => k['Issue Type'] === 'Bug').length;
      if (sCount + bCount === 0) { epicGrobS += epicPlan / 2; epicGrobB += epicPlan / 2; }
      else {
        epicGrobS += epicPlan * (sCount / (sCount + bCount));
        epicGrobB += epicPlan * (bCount / (sCount + bCount));
      }
    }
  }

  // ── 4) Detailplanung (Refinement) — VOLLSTÄNDIGER Refinement-Plan ───
  // Inkl. bereits abgeschlossener Items, damit Detail der ganze Plan
  // ist und Ist eine echte Teilmenge davon (= Burndown-Logik).
  // Status-Filter:
  //   • REFINED_PLUS (Backlog/Ready/…/In Test) → immer
  //   • DONE          (Closed/Resolved/Aborted) → nur wenn Resolved
  //                     im Release-Fenster (sonst alte Phantom-Items)
  // Zusätzlich: Created ≤ Release-Ende (Items, die später entstanden,
  // gehören nicht zur Detail-Säule dieses Releases).
  let refS = 0, refB = 0, nRefS = 0, nRefB = 0, nRefSpS = 0, nRefSpB = 0;
  let nRefOpen = 0, nRefDone = 0;
  let nRefEligible = 0, nRefMissingEst = 0; // für Daten-Qualitäts-Warnung
  let nRefCapped = 0; // Items, deren Estimate auf 10 PT gekappt wurde
  for (const r of rows) {
    if (r['Issue Type'] !== 'Story' && r['Issue Type'] !== 'Bug') continue;
    if (!issueInBlock(r, true) || !matchTeam(r)) continue; // strict: eigene Fix Version
    const sKey = statusKey(r['Status']);
    const isOpen = REFINED_PLUS.has(sKey);
    const isDoneStatus = DONE_STATUSES.has(sKey);
    if (!isOpen && !isDoneStatus) continue;
    if (!createdBeforeReleaseEnd(r)) { nRefSkippedOutOfRange++; continue; }
    if (isDoneStatus && !resolvedInRange(r)) continue;
    nRefEligible++;
    // Nur Σ Original Estimate; Story Points werden ignoriert.
    const est = secondsToDays(r['Σ Original Estimate']);
    const raw = (est != null && est > 0) ? est : 0;
    if (raw <= 0) { nRefMissingEst++; continue; }
    if (raw > ITEM_PT_CAP) nRefCapped++;
    const v = cap(raw);
    if (r['Issue Type'] === 'Story') { refS += v; nRefS++; nRefSpS++; }
    else                             { refB += v; nRefB++; nRefSpB++; }
    if (isOpen) nRefOpen++; else nRefDone++;
  }

  // ── 5) Ist Aufwand (Σ Original Estimate der done-Items) ─────────────
  // User-Entscheidung:
  //   • Time Spent / Σ Time Spent komplett ignorieren (Aggregat-Outliers).
  //   • Story Points komplett ignorieren (auf User-Wunsch).
  // → Ist pro Item = Σ Original Estimate. Items ohne Estimate fallen raus.
  // Haupt-Ist-Säule: Σ Original Estimate (SP & Time Spent: aus).
  let istS = 0, istB = 0, nIstS = 0, nIstB = 0;
  let nIstNoPlan = 0;     // Items resolved im Fenster, aber ohne Estimate
  let nIstEligible = 0;   // Items resolved im Fenster total (für Coverage)
  let nIstEstCapped = 0;  // Items, deren Item-Estimate auf 10 PT gekappt wurde
  let nIstTsCapped = 0;   // Items, deren Item-Time-Spent auf 10 PT gekappt wurde

  // Variante A (Mix): Time Spent priorisiert, Original Estimate als Fallback.
  let tsOrEstS = 0, tsOrEstB = 0;
  let nTsS = 0, nTsB = 0;
  let nEstFallbackS = 0, nEstFallbackB = 0;
  let nNeitherS = 0, nNeitherB = 0;

  // Variante B (nur TS): Items ohne TS tragen 0 bei.
  let tsOnlyS = 0, tsOnlyB = 0;
  let nNoTsS = 0, nNoTsB = 0;

  const SEC_PER_PT = 28800;

  for (const r of rows) {
    if (!isDone(r['Status'] ?? '')) continue;
    if (r['Issue Type'] !== 'Story' && r['Issue Type'] !== 'Bug') continue;
    if (!issueInBlock(r, true) || !matchTeam(r)) continue; // strict: eigene Fix Version
    if (!resolvedInRange(r)) { nIstSkippedOutOfRange++; continue; }
    nIstEligible++;
    const isStory = r['Issue Type'] === 'Story';

    // Werte für dieses Item:
    const estRaw = secondsToDays(r['Σ Original Estimate']) ?? 0;
    if (estRaw > ITEM_PT_CAP) nIstEstCapped++;
    const estPT = estRaw > 0 ? cap(estRaw) : 0;
    // Time Spent: bevorzugt 'Σ Time Spent' (Aggregat über Sub-Tasks),
    // weil im SchueCal niemand auf Story/Bug-Ebene direkt loggt — alle
    // Zeiten landen auf Sub-Tasks. Item-Level 'Time Spent' bleibt als
    // Zweitquelle, falls doch mal direkt geloggt wurde.
    // Der frühere Outlier-Pfad (z.B. CAL-94568 mit 977 PT in Σ TS) ist
    // durch den 10-PT-Cap pro Item entschärft.
    const tsSumSec  = num(r['Σ Time Spent']);
    const tsItemSec = num(r['Time Spent']);
    const tsSec = (tsSumSec != null && tsSumSec > 0)
      ? tsSumSec
      : (tsItemSec != null && tsItemSec > 0 ? tsItemSec : 0);
    const tsRaw = tsSec > 0 ? tsSec / SEC_PER_PT : 0;
    if (tsRaw > ITEM_PT_CAP) nIstTsCapped++;
    const tsPT = tsRaw > 0 ? cap(tsRaw) : 0;

    // ── Haupt-Ist-Säule (nur Estimate) ───────────────────────────────
    if (estPT > 0) {
      if (isStory) { istS += estPT; nIstS++; }
      else         { istB += estPT; nIstB++; }
    } else {
      nIstNoPlan++;
    }

    // ── Variante A: TS oder Estimate-Fallback ──────────────────────
    if (tsPT > 0) {
      if (isStory) { tsOrEstS += tsPT; nTsS++; } else { tsOrEstB += tsPT; nTsB++; }
    } else if (estPT > 0) {
      if (isStory) { tsOrEstS += estPT; nEstFallbackS++; } else { tsOrEstB += estPT; nEstFallbackB++; }
    } else {
      if (isStory) nNeitherS++; else nNeitherB++;
    }

    // ── Variante B: nur TS, ohne Fallback ──────────────────────────
    if (tsPT > 0) {
      if (isStory) tsOnlyS += tsPT; else tsOnlyB += tsPT;
    } else {
      if (isStory) nNoTsS++; else nNoTsB++;
    }
  }
  const istTotal   = istS + istB;
  const kapaTotal  = capStories + capBugs;
  const roughTotal = epicGrobS + epicGrobB;
  const detTotal   = refS + refB;
  const vsKapaPct   = kapaTotal  > 0 ? (istTotal / kapaTotal)  * 100 : null;
  const vsRoughPct  = roughTotal > 0 ? (istTotal / roughTotal) * 100 : null;
  const vsDetailPct = detTotal   > 0 ? (istTotal / detTotal)   * 100 : null;

  // ── Tooltips ────────────────────────────────────────────────────────
  const scopeText = (groupMode === 'team' && blockReleaseFilter)
    ? `Team „${blockName}" · Release „${blockReleaseFilter}"`
    : (groupMode === 'team'
        ? `Team „${blockName}" · Σ alle Releases`
        : `Release „${blockName}"`);
  // SP-Faktor wird aktuell nicht angewendet (Story Points sind in der
  // Berechnung deaktiviert) — Hinweis zur Klarheit.
  const factorNote = `\nHinweis: Story Points werden NICHT in die Berechnung einbezogen. Nur Σ Original Estimate zählt.${sp2pt !== 1.0 ? ` (SP-Faktor ${sp2pt} ist gesetzt, hat aber aktuell keine Wirkung.)` : ''}`;

  const tipKapa = `Kapazität aus „Kapa verwalten" (Team × Release × Monat, getrennt Stories/Bugs).\n`
    + `Scope: ${scopeText}.\n\nStories: ${fmtInt(capStories)} PT · Bugs: ${fmtInt(capBugs)} PT`;
  const tipInitial = `Säulenplanung = Initial-Plan, manuell überschreibbar.\n`
    + `Default = Kapazität (solange du nichts einträgst, ist dieser Wert identisch zur Kapa-Säule).\n`
    + `Sobald Werte gepflegt sind, ist diese Säule unabhängig von der Kapa.\n\nScope: ${scopeText}.`;
  const tipKapaPlan = `${tipKapa}\n\n— Solange kein manueller Plan gepflegt ist, sind Kapazität und Säulenplanung identisch und werden hier kombiniert.`;
  const nEpicsRoughOnly = nEpicsRough - nEpicsBoth;
  const nEpicsEstOnly   = nEpicsEst   - nEpicsBoth;
  const tipEpicGrob = `Pro Epic: Rough Estimation (d) + Σ Original Estimate (PT) — beide Felder werden summiert.\n`
    + `Aufteilung Stories/Bugs proportional zum Σ Original Estimate der Kids; Fallback auf Kid-Count, wenn keine Kid-Estimates gepflegt sind.${factorNote}\n\n`
    + `Quellen-Mix der Epics in diesem Block:\n`
    + `• ${nEpicsBoth} Epic(s) mit Rough UND Estimate (= Summe wirkt)\n`
    + `• ${nEpicsRoughOnly} Epic(s) nur mit Rough\n`
    + `• ${nEpicsEstOnly} Epic(s) nur mit Estimate\n\n`
    + `Scope: ${scopeText}.\n\nWarum oft hoch: zählt auch Future-Epics ohne refined Items.`;
  const refTimeWindowText = hasTimeWindow
    ? `\nNur Items mit Created ≤ ${istCal?.end ?? '?'} (Release-Ende). Karteileichen, deren Fix Version nachträglich auf den Release zeigt, sind ausgeschlossen.`
    : '';
  const refSkippedNote = nRefSkippedOutOfRange > 0
    ? `\n\n${nRefSkippedOutOfRange} weitere Items wurden NICHT mitgezählt, weil ihr Created-Datum nach Release-Ende liegt.`
    : '';
  const refCapNote = nRefCapped > 0
    ? `\n\nOutlier-Cap: ${nRefCapped} Item(s) hatten >${ITEM_PT_CAP} PT Estimate und wurden auf ${ITEM_PT_CAP} PT gekappt.`
    : `\n\nOutlier-Cap: jedes Item zählt max. ${ITEM_PT_CAP} PT (kein Item musste gekappt werden).`;
  const tipRefine = `Σ „Σ Original Estimate" über ${nRefS + nRefB} Items.\n`
    + `Quelle: nur Stories/Bugs mit eigener Fix Version, die das Release zeigt (Item-Level, kein Parent-Fallback). Rough Estimation und Estimate auf Epic-Ebene fließen NICHT ein — die zählen nur in der Grobplanung-Säule.\n`
    + `Status: offene Items (Backlog/Ready/In Progress/Codereview/Integration/Awaiting Test/In Test) + bereits abgeschlossene Items (Closed/Resolved/Aborted im Release-Fenster) → vollständiger Refinement-Plan.\n`
    + `\n• ${nRefOpen} Items offen + ${nRefDone} Items done = ${nRefS + nRefB} Σ\n`
    + `• Stories: ${nRefS} Items\n`
    + `• Bugs:    ${nRefB} Items${factorNote}${refCapNote}\n\nScope: ${scopeText}.${refTimeWindowText}${refSkippedNote}\n\n`
    + `Lese-Hilfe: Detail ist der vollständige Plan, Ist ist die Teilmenge davon, die schon fertig ist. Detail − Ist = noch ausstehende Arbeit.`;
  const istTimeWindowText = hasTimeWindow
    ? `Resolved-Datum muss im Release-Fenster ${istCal?.start ?? '?'} → ${istCal?.end ?? '?'} liegen.`
    : `Kein Release-Fenster gepflegt → alle geschlossenen Items werden gezählt.`;
  const skippedNote = nIstSkippedOutOfRange > 0
    ? `\n${nIstSkippedOutOfRange} Items mit Fix Version „${istReleaseName}" wurden NICHT mitgezählt — Resolved-Datum außerhalb des Release-Fensters.`
    : '';
  const noPlanNote = nIstNoPlan > 0
    ? `\n${nIstNoPlan} Items im Fenster hatten kein Original Estimate gepflegt und konnten daher nicht in den Ist-Wert einfließen.`
    : '';
  const pctLine = (label: string, p: number | null, base: number) =>
    p == null ? `${label}: – (Basis 0)` : `${label}: ${Math.round(p)} % (= ${fmtInt(istTotal)} / ${fmtInt(base)} PT)`;
  const istCapNote = (nIstEstCapped > 0 || nIstTsCapped > 0)
    ? `\nOutlier-Cap: ${nIstEstCapped} Item(s) Estimate, ${nIstTsCapped} Item(s) Time Spent wurden auf ${ITEM_PT_CAP} PT gekappt.`
    : `\nOutlier-Cap: jedes Item zählt max. ${ITEM_PT_CAP} PT (kein Item musste gekappt werden).`;
  const tipIst = `Σ Ist-Aufwand über ${nIstS + nIstB} geschlossene Items (Status Closed/Resolved/Aborted).\n`
    + `Quelle: nur Stories/Bugs mit eigener Fix Version, die das Release zeigt (Item-Level, kein Parent-Fallback). Pro Item: Σ Original Estimate (Sekunden → PT). Story Points UND Time Spent werden nicht ausgewertet.\n`
    + `\n• Stories: ${nIstS} Items\n• Bugs:    ${nIstB} Items\n\nScope: ${scopeText}.\n${istTimeWindowText}${skippedNote}${noPlanNote}${istCapNote}\n\n`
    + `Verhältnis zum Plan-Funnel:\n`
    + ` • ${pctLine('vs. Kapa',  vsKapaPct,   kapaTotal)}\n`
    + ` • ${pctLine('vs. Rough', vsRoughPct,  roughTotal)}\n`
    + ` • ${pctLine('vs. Detail',vsDetailPct, detTotal)}\n\n`
    + `Lese-Hilfe: <100 % der Kapa = Kapa nicht ausgeschöpft; >100 % = überzogen. <100 % vs. Detail = noch Items offen; >100 % = Aufwand höher als Detailschätzung.`;

  // ── Sub-Labels ──────────────────────────────────────────────────────
  const subEpicGrob = `${nEpicsRough + nEpicsEstOnly} Epic(s): ${nEpicsBoth} mit Rough+Est · ${nEpicsRoughOnly} nur Rough · ${nEpicsEstOnly} nur Est`;
  const subRefine = `${nRefOpen} offen · ${nRefDone} done${nRefSkippedOutOfRange > 0 ? ` · ${nRefSkippedOutOfRange} außerhalb` : ''}`;
  const subIst = (() => {
    const parts: string[] = [];
    if (hasTimeWindow) {
      parts.push(`${nIstS + nIstB} Items resolved im Fenster`);
      if (nIstSkippedOutOfRange > 0) parts.push(`${nIstSkippedOutOfRange} außerhalb`);
    } else {
      parts.push(`${nIstS + nIstB} Items closed/resolved`);
    }
    if (nIstNoPlan > 0) parts.push(`${nIstNoPlan} ohne Schätzung`);
    return parts.join(' · ');
  })();

  // ── Details-Listen pro Säule (permanent sichtbar unter der Säule) ──
  const kapaDetails: string[] = [
    `Quelle: „Kapa verwalten"-Modal`,
    `Aufteilung: Team × Release × Monat × {Stories, Bugs}`,
  ];
  const initialDetails: string[] = [
    `Manuell gepflegter Wert oder Default = Kapazität`,
    `Ohne Eintrag = identisch zur Kapa-Säule`,
  ];
  const grobDetails: string[] = [
    `Quelle: Epic-Ebene`,
    `Pro Epic: „Rough Estimation (d)" + „Σ Original Estimate"`,
    `Status: alle Epics mit eigener Fix Version → Release`,
    `Aufteilung S/B: anteilig zu Σ Estimate der Child items`,
  ];
  const refineDetails: string[] = [
    `🟦 Vollständiger Refinement-Plan dieses Releases = ${nRefOpen} noch offene Items + ${nRefDone} bereits done Items im Fenster.`,
    `Die Ist-Säule ist genau die done-Teilmenge davon → Detail − Ist = noch ausstehende Arbeit.`,
    `Quelle: Stories + Bugs mit eigener Fix Version → Release (kein Parent-Fallback)`,
    `Wert pro Item: „Σ Original Estimate" (SP & Time Spent: aus)`,
    `Outlier-Cap: jedes Item zählt max. ${ITEM_PT_CAP} PT${nRefCapped > 0 ? ` — ${nRefCapped} Item(s) wurden gekappt` : ''}`,
    `Status offen (zählt immer): Backlog · Ready · In Progress · Codereview · In Integration · Awaiting Test · In Test`,
    `Status done (zählt, wenn Resolved im Release-Fenster): Closed · Resolved · Aborted`,
    `NICHT enthalten: Awaiting Refinement · New · Open · Rough/Estimate der Epics`,
    `Items ohne Σ Original Estimate fallen raus`,
  ];
  const istDetails: string[] = [
    `Quelle: Stories + Bugs mit eigener Fix Version → Release (kein Parent-Fallback)`,
    `Wert pro Item: „Σ Original Estimate" (SP & Time Spent: aus)`,
    `Outlier-Cap: jedes Item zählt max. ${ITEM_PT_CAP} PT${nIstEstCapped > 0 ? ` — ${nIstEstCapped} Item(s) wurden gekappt` : ''}`,
    `Status: Closed · Resolved · Aborted`,
    hasTimeWindow
      ? `Resolved-Datum muss im Release-Fenster ${istCal?.start ?? ''} → ${istCal?.end ?? ''} liegen`
      : `Kein Release-Fenster gepflegt — alle done-Items zählen`,
    `NICHT enthalten: alte Items, die nachträglich die Fix Version dieses Releases bekamen, aber davor resolved wurden`,
    `Ist ist eine Teilmenge der Detailplanung — Detail − Ist = noch ausstehende Arbeit`,
  ];

  // ── Säulen zusammenstellen ──────────────────────────────────────────
  const cols: Column[] = [];
  if (ipRaw) {
    cols.push({ key: 'kapa',    label: 'Kapazität',     stories: capStories,  bugs: capBugs,  tooltip: tipKapa, detailsLines: kapaDetails });
    cols.push({ key: 'initial', label: 'Säulenplanung', stories: initStories, bugs: initBugs, editable: true, initKey, tooltip: tipInitial, subLabel: 'manuell', detailsLines: initialDetails });
  } else {
    cols.push({ key: 'kapaPlan', label: 'Kapazität / Säulenplanung', stories: capStories, bugs: capBugs, editable: true, initKey, isDefault: true, tooltip: tipKapaPlan, detailsLines: [...kapaDetails, ...initialDetails] });
  }
  // ── Daten-Qualitäts-Warnungen ───────────────────────────────────────
  // Wenn weniger als 50 % der relevanten Items ein Estimate haben, ist
  // die Säulen-Aussage statistisch dünn — wir blenden eine Warnung ein.
  const refCoverage = nRefEligible > 0 ? (nRefEligible - nRefMissingEst) / nRefEligible : 1;
  const istCoverage = nIstEligible > 0 ? (nIstEligible - nIstNoPlan)   / nIstEligible : 1;
  const refWarn = (nRefEligible > 0 && refCoverage < 0.5)
    ? `Nur ${nRefEligible - nRefMissingEst} von ${nRefEligible} Items (${Math.round(refCoverage * 100)} %) haben ein Σ Original Estimate gepflegt. Die Säule zeigt nicht das volle Bild — bitte Refinement nachpflegen, sonst ist die Plan-Zahl wenig aussagekräftig.`
    : undefined;
  const istWarn = (nIstEligible > 0 && istCoverage < 0.5)
    ? `Nur ${nIstEligible - nIstNoPlan} von ${nIstEligible} done-Items (${Math.round(istCoverage * 100)} %) haben ein Σ Original Estimate gepflegt. Die Säule zeigt nicht das volle Bild — der echte Verbrauch ist vermutlich höher.`
    : undefined;

  cols.push({ key: 'epicGrob', label: 'Grobplanung (Epic-Ebene)',   stories: epicGrobS, bugs: epicGrobB, tooltip: tipEpicGrob, subLabel: subEpicGrob, detailsLines: grobDetails });
  cols.push({ key: 'refine',   label: 'Detailplanung (Refinement)', stories: refS,      bugs: refB,      tooltip: tipRefine,   subLabel: subRefine,   detailsLines: refineDetails, qualityWarning: refWarn, nRefCapped, itemPtCap: ITEM_PT_CAP });
  cols.push({
    key: 'ist',
    label: 'Ist Aufwand',
    stories: istS,
    bugs: istB,
    tooltip: tipIst,
    subLabel: subIst,
    detailsLines: istDetails,
    qualityWarning: istWarn,
    vsKapaPct,
    vsRoughPct,
    vsDetailPct,
    nFromTime: 0,         // Time Spent wird grundsätzlich nicht mehr ausgewertet
    nFromPlan: nIstS + nIstB,
    // Variante A: TS bevorzugt, Estimate als Fallback
    tsOrEstStoriesPT: tsOrEstS,
    tsOrEstBugsPT:    tsOrEstB,
    nTsS, nTsB,
    nEstFallbackS, nEstFallbackB,
    nNeitherS, nNeitherB,
    // Variante B: nur TS
    tsOnlyStoriesPT: tsOnlyS,
    tsOnlyBugsPT:    tsOnlyB,
    nNoTsS, nNoTsB,

    nIstDoneTotal: nIstEligible,
    nIstEstCapped, nIstTsCapped, itemPtCap: ITEM_PT_CAP,
  });
  return cols;
}
